import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Cluster,
  ClusterSetting,
  ClusterTemplate,
  CreateClusterParams,
  CreateClusterResponse,
  CreateNodegroupParams,
  CreateNodegroupResponse,
  DeleteClusterParams,
  DeleteClusterResponse,
  DeleteNodegroupParams,
  GetClusterParams,
  GetClusterResponse,
  GetClusterSettingsParams,
  GetClusterSettingsResponse,
  GetClusterTemplateParams,
  GetClusterTemplateResponse,
  GetClusterTemplateSettingsParams,
  GetClusterTemplateSettingsResponse,
  GetClusterTemplatesParams,
  GetClusterTemplatesResponse,
  GetClustersParams,
  GetClustersResponse,
  GetNodegroupParams,
  GetNodegroupResponse,
  ListNodegroupsParams,
  ListNodegroupsResponse,
  Nodegroup,
  ResizeClusterParams,
  ResizeClusterResponse,
  RotateClusterCAParams,
  UpdateClusterSettingsParams,
  UpdateClusterSettingsResponse,
  UpdateNodegroupParams,
  UpdateNodegroupResponse,
  UpgradeClusterParams,
  UpgradeClusterResponse
} from "./types";

export type ClustersState = {
  clusters: Cluster[] | null;
  cluster: Cluster | null;
  isClusterLoading: boolean;
  areClustersLoading: boolean;
  isClusterCreating: boolean;
  isClusterResizing: boolean;
  isClusterUpgrading: boolean;
  isClusterDeleting: boolean;
  isClusterCARotating: boolean;
  clusterTemplates: ClusterTemplate[] | null;
  clusterTemplate: ClusterTemplate | null;
  isClusterTemplateLoading: boolean;
  areClusterTemplatesLoading: boolean;
  nodegroups: Nodegroup[] | null;
  areNodegroupsLoading: boolean;
  isNodegroupCreating: boolean;
  isNodegroupUpdating: boolean;
  isNodegroupDeleting: boolean;
  selectedNodegroupDetails: Nodegroup | null;
  isNodegroupDetailsLoading: boolean;
  clusterSettings: ClusterSetting[] | null;
  clusterTemplateSettings: ClusterSetting[] | null;
  areClusterSettingsLoading: boolean;
  areClusterTemplateSettingsLoading: boolean;
  isClusterSettingsUpdating: boolean;
};

const initialState: ClustersState = {
  clusters: null,
  cluster: null,
  isClusterLoading: false,
  areClustersLoading: false,
  isClusterCreating: false,
  isClusterResizing: false,
  isClusterUpgrading: false,
  isClusterDeleting: false,
  isClusterCARotating: false,
  clusterTemplates: null,
  clusterTemplate: null,
  isClusterTemplateLoading: false,
  areClusterTemplatesLoading: false,
  nodegroups: null,
  areNodegroupsLoading: false,
  isNodegroupCreating: false,
  isNodegroupUpdating: false,
  isNodegroupDeleting: false,
  selectedNodegroupDetails: null,
  isNodegroupDetailsLoading: false,
  clusterSettings: null,
  clusterTemplateSettings: null,
  areClusterSettingsLoading: false,
  areClusterTemplateSettingsLoading: false,
  isClusterSettingsUpdating: false
};

const clustersSlice = createSlice({
  name: "clusters",
  initialState,
  reducers: {
    // Get Cluster
    getClusterRequest: (state, action: PayloadAction<GetClusterParams>) => {
      state.isClusterLoading = true;
    },
    getClusterSuccess: (state, action: PayloadAction<GetClusterResponse>) => {
      state.cluster = action.payload;
      state.isClusterLoading = false;
    },
    getClusterFailure: (
      state,
      action: PayloadAction<{ params: GetClusterParams; error: string }>
    ) => {
      state.isClusterLoading = false;
    },

    // Get Clusters
    getClustersRequest: (state, action: PayloadAction<GetClustersParams>) => {
      state.areClustersLoading = true;
    },
    getClustersSuccess: (state, action: PayloadAction<GetClustersResponse>) => {
      state.clusters = action.payload;
      state.areClustersLoading = false;
    },
    getClustersFailure: (
      state,
      action: PayloadAction<{ params: GetClustersParams; error: string }>
    ) => {
      state.areClustersLoading = false;
    },

    // Create Cluster
    createClusterRequest: (
      state,
      action: PayloadAction<CreateClusterParams>
    ) => {
      state.isClusterCreating = true;
    },
    createClusterSuccess: (
      state,
      action: PayloadAction<CreateClusterResponse>
    ) => {
      state.isClusterCreating = false;
    },
    createClusterFailure: (
      state,
      action: PayloadAction<{ params: CreateClusterParams; error: string }>
    ) => {
      state.isClusterCreating = false;
    },

    // Resize Cluster
    resizeClusterRequest: (
      state,
      action: PayloadAction<ResizeClusterParams>
    ) => {
      state.isClusterResizing = true;
    },
    resizeClusterSuccess: (
      state,
      action: PayloadAction<ResizeClusterResponse>
    ) => {
      state.isClusterResizing = false;
    },
    resizeClusterFailure: (
      state,
      action: PayloadAction<{ params: ResizeClusterParams; error: string }>
    ) => {
      state.isClusterResizing = false;
    },

    // Upgrade Cluster
    upgradeClusterRequest: (
      state,
      action: PayloadAction<UpgradeClusterParams>
    ) => {
      state.isClusterUpgrading = true;
    },
    upgradeClusterSuccess: (
      state,
      action: PayloadAction<UpgradeClusterResponse>
    ) => {
      state.isClusterUpgrading = false;
    },
    upgradeClusterFailure: (
      state,
      action: PayloadAction<{ params: UpgradeClusterParams; error: string }>
    ) => {
      state.isClusterUpgrading = false;
    },

    // Delete Cluster
    deleteClusterRequest: (
      state,
      action: PayloadAction<DeleteClusterParams>
    ) => {
      state.isClusterDeleting = true;
    },
    deleteClusterSuccess: (
      state,
      action: PayloadAction<DeleteClusterResponse>
    ) => {
      state.isClusterDeleting = false;
    },
    deleteClusterFailure: (
      state,
      action: PayloadAction<{ params: DeleteClusterParams; error: string }>
    ) => {
      state.isClusterDeleting = false;
    },

    // Get Cluster Template
    getClusterTemplateRequest: (
      state,
      action: PayloadAction<GetClusterTemplateParams>
    ) => {
      state.isClusterTemplateLoading = true;
    },
    getClusterTemplateSuccess: (
      state,
      action: PayloadAction<GetClusterTemplateResponse>
    ) => {
      state.clusterTemplate = action.payload;
      state.isClusterTemplateLoading = false;
    },
    getClusterTemplateFailure: (
      state,
      action: PayloadAction<{ params: GetClusterTemplateParams; error: string }>
    ) => {
      state.isClusterTemplateLoading = false;
    },

    // Get Cluster Templates
    getClusterTemplatesRequest: (
      state,
      action: PayloadAction<GetClusterTemplatesParams>
    ) => {
      state.areClusterTemplatesLoading = true;
    },
    getClusterTemplatesSuccess: (
      state,
      action: PayloadAction<GetClusterTemplatesResponse>
    ) => {
      state.clusterTemplates = action.payload;
      state.areClusterTemplatesLoading = false;
    },
    getClusterTemplatesFailure: (
      state,
      action: PayloadAction<{
        params: GetClusterTemplatesParams;
        error: string;
      }>
    ) => {
      state.areClusterTemplatesLoading = false;
    },

    // Rotate Cluster CA
    rotateClusterCARequest: (
      state,
      action: PayloadAction<RotateClusterCAParams>
    ) => {
      state.isClusterCARotating = true;
    },
    rotateClusterCASuccess: (state) => {
      state.isClusterCARotating = false;
    },
    rotateClusterCAFailure: (
      state,
      action: PayloadAction<{ params: RotateClusterCAParams; error: string }>
    ) => {
      state.isClusterCARotating = false;
    },

    // List Nodegroups
    listNodegroupsRequest: (
      state,
      action: PayloadAction<ListNodegroupsParams>
    ) => {
      state.areNodegroupsLoading = true;
    },
    listNodegroupsSuccess: (
      state,
      action: PayloadAction<ListNodegroupsResponse>
    ) => {
      state.nodegroups = action.payload;
      state.areNodegroupsLoading = false;
    },
    listNodegroupsFailure: (
      state,
      action: PayloadAction<{ params: ListNodegroupsParams; error: string }>
    ) => {
      state.areNodegroupsLoading = false;
    },

    // Get Nodegroup
    getNodegroupRequest: (state, action: PayloadAction<GetNodegroupParams>) => {
      state.selectedNodegroupDetails = null;
      state.isNodegroupDetailsLoading = true;
    },
    getNodegroupSuccess: (
      state,
      action: PayloadAction<GetNodegroupResponse>
    ) => {
      state.selectedNodegroupDetails = action.payload;
      state.isNodegroupDetailsLoading = false;
    },
    getNodegroupFailure: (
      state,
      action: PayloadAction<{ params: GetNodegroupParams; error: string }>
    ) => {
      state.selectedNodegroupDetails = null;
      state.isNodegroupDetailsLoading = false;
    },

    // Create Nodegroup
    createNodegroupRequest: (
      state,
      action: PayloadAction<CreateNodegroupParams>
    ) => {
      state.isNodegroupCreating = true;
    },
    createNodegroupSuccess: (
      state,
      action: PayloadAction<CreateNodegroupResponse>
    ) => {
      state.isNodegroupCreating = false;
    },
    createNodegroupFailure: (
      state,
      action: PayloadAction<{ params: CreateNodegroupParams; error: string }>
    ) => {
      state.isNodegroupCreating = false;
    },

    // Update Nodegroup
    updateNodegroupRequest: (
      state,
      action: PayloadAction<UpdateNodegroupParams>
    ) => {
      state.isNodegroupUpdating = true;
    },
    updateNodegroupSuccess: (
      state,
      action: PayloadAction<UpdateNodegroupResponse>
    ) => {
      state.isNodegroupUpdating = false;
    },
    updateNodegroupFailure: (
      state,
      action: PayloadAction<{ params: UpdateNodegroupParams; error: string }>
    ) => {
      state.isNodegroupUpdating = false;
    },

    // Delete Nodegroup
    deleteNodegroupRequest: (
      state,
      action: PayloadAction<DeleteNodegroupParams>
    ) => {
      state.isNodegroupDeleting = true;
    },
    deleteNodegroupSuccess: (state) => {
      state.isNodegroupDeleting = false;
    },
    deleteNodegroupFailure: (
      state,
      action: PayloadAction<{ params: DeleteNodegroupParams; error: string }>
    ) => {
      state.isNodegroupDeleting = false;
    },

    // Get Cluster Settings
    getClusterSettingsRequest: (
      state,
      action: PayloadAction<GetClusterSettingsParams>
    ) => {
      state.areClusterSettingsLoading = true;
    },
    getClusterSettingsSuccess: (
      state,
      action: PayloadAction<GetClusterSettingsResponse>
    ) => {
      state.clusterSettings = action.payload.settings;
      state.areClusterSettingsLoading = false;
    },
    getClusterSettingsFailure: (
      state,
      action: PayloadAction<{ params: GetClusterSettingsParams; error: string }>
    ) => {
      state.areClusterSettingsLoading = false;
    },

    // Get Cluster Template Settings
    getClusterTemplateSettingsRequest: (
      state,
      action: PayloadAction<GetClusterTemplateSettingsParams>
    ) => {
      state.clusterTemplateSettings = null;
      state.areClusterTemplateSettingsLoading = true;
    },
    getClusterTemplateSettingsSuccess: (
      state,
      action: PayloadAction<GetClusterTemplateSettingsResponse>
    ) => {
      state.clusterTemplateSettings = action.payload.settings;
      state.areClusterTemplateSettingsLoading = false;
    },
    getClusterTemplateSettingsFailure: (
      state,
      action: PayloadAction<{
        params: GetClusterTemplateSettingsParams;
        error: string;
      }>
    ) => {
      state.clusterTemplateSettings = null;
      state.areClusterTemplateSettingsLoading = false;
    },

    // Update Cluster Settings
    updateClusterSettingsRequest: (
      state,
      action: PayloadAction<UpdateClusterSettingsParams>
    ) => {
      state.isClusterSettingsUpdating = true;
    },
    updateClusterSettingsSuccess: (
      state,
      action: PayloadAction<{
        params: UpdateClusterSettingsParams;
        result: UpdateClusterSettingsResponse;
      }>
    ) => {
      state.isClusterSettingsUpdating = false;
      // Optimistically apply changed labels to local settings
      state.clusterSettings = state.clusterSettings
        ? state.clusterSettings.map((s) =>
            action.payload.params.data.labels[s.key] !== undefined
              ? {
                  ...s,
                  value: action.payload.params.data.labels[s.key],
                  exists_in_cluster: true
                }
              : s
          )
        : null;
    },
    updateClusterSettingsFailure: (
      state,
      action: PayloadAction<{
        params: UpdateClusterSettingsParams;
        error: string;
      }>
    ) => {
      state.isClusterSettingsUpdating = false;
    },

    // Clear
    clearClusterTemplateSettings: (state) => {
      state.clusterTemplateSettings = null;
      state.areClusterTemplateSettingsLoading = false;
    },
    clearSelectedNodegroup: (state) => {
      state.selectedNodegroupDetails = null;
      state.isNodegroupDetailsLoading = false;
    },
    clear: () => initialState
  }
});

export const {
  getClusterRequest,
  getClusterSuccess,
  getClusterFailure,
  getClustersRequest,
  getClustersSuccess,
  getClustersFailure,
  createClusterRequest,
  createClusterSuccess,
  createClusterFailure,
  resizeClusterRequest,
  resizeClusterSuccess,
  resizeClusterFailure,
  upgradeClusterRequest,
  upgradeClusterSuccess,
  upgradeClusterFailure,
  deleteClusterRequest,
  deleteClusterSuccess,
  deleteClusterFailure,
  getClusterTemplateRequest,
  getClusterTemplateSuccess,
  getClusterTemplateFailure,
  getClusterTemplatesRequest,
  getClusterTemplatesSuccess,
  getClusterTemplatesFailure,
  rotateClusterCARequest,
  rotateClusterCASuccess,
  rotateClusterCAFailure,
  listNodegroupsRequest,
  listNodegroupsSuccess,
  listNodegroupsFailure,
  getNodegroupRequest,
  getNodegroupSuccess,
  getNodegroupFailure,
  createNodegroupRequest,
  createNodegroupSuccess,
  createNodegroupFailure,
  updateNodegroupRequest,
  updateNodegroupSuccess,
  updateNodegroupFailure,
  deleteNodegroupRequest,
  deleteNodegroupSuccess,
  deleteNodegroupFailure,
  getClusterSettingsRequest,
  getClusterSettingsSuccess,
  getClusterSettingsFailure,
  getClusterTemplateSettingsRequest,
  getClusterTemplateSettingsSuccess,
  getClusterTemplateSettingsFailure,
  updateClusterSettingsRequest,
  updateClusterSettingsSuccess,
  updateClusterSettingsFailure,
  clearClusterTemplateSettings,
  clearSelectedNodegroup,
  clear
} = clustersSlice.actions;

export const { actions: clustersActions } = clustersSlice;

export const reducer = clustersSlice.reducer;
